import React from "react";
import { Icon } from "./siIcons";
import { siApi } from "./siApi";

// Notes et commentaires de l'onglet Système (demande d'Anthony du
// 20/10/2026) : sur les applications et services et sur les machines
// virtuelles. Une note libre par élément (création et dernière modification
// horodatées et signées) et un fil de commentaires datés et signés ; chacun
// ne modifie ou ne supprime que les siens. Backend : siController
// (resumeAnnotations, lireAnnotations, enregistrerNote, *Commentaire).
// cible = "application" (ref = clé) ou "vm" (ref = _id de la ligne).

// Nom affiché tout de suite sur une note ou un commentaire, en attendant celui
// renvoyé par le serveur (même forme : « Prénom NOM »).
function moi() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return [u?.name, (u?.lastName || "").toUpperCase()].filter(Boolean).join(" ") || "Vous";
  } catch (e) {
    return "Vous";
  }
}

const dateHeure = (iso) =>
  iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).replace(" ", " à ") : "";

// Pastilles des lignes d'un tableau : { [ref]: { note, commentaires } }.
// cible null = tableau sans annotations (aucun appel).
export function useAnnotations(cible, notifier) {
  const [resume, setResume] = React.useState({});
  React.useEffect(() => {
    if (!cible) return undefined;
    let actif = true;
    siApi
      .resumeAnnotations(cible)
      .then((r) => actif && setResume(r || {}))
      .catch((e) => actif && notifier?.(e.message, { type: "erreur" }));
    return () => {
      actif = false;
    };
  }, [cible, notifier]);
  const maj = React.useCallback((ref, valeur) => setResume((r) => ({ ...r, [ref]: valeur })), []);
  return [resume, maj];
}

// Bouton « bulle » en bout de ligne : nombre de commentaires, point si une note existe.
export function BoutonAnnotations({ resume, libelle, onClick }) {
  const n = resume?.commentaires || 0;
  const note = !!resume?.note;
  const titre = [note && "Note", n && `${n} commentaire${n > 1 ? "s" : ""}`].filter(Boolean).join(" · ") || "Ajouter une note ou un commentaire";
  return (
    <button className={`mdt-ic six-an-btn ${n || note ? "is-on" : ""}`} title={titre} aria-label={`Notes et commentaires — ${libelle}`} onClick={onClick}>
      <Icon name="message" size={13} />
      {n > 0 && <span className="six-an-n">{n}</span>}
      {note && !n && <i className="six-an-dot" />}
    </button>
  );
}

function Commentaire({ c, onModifier, onSupprimer }) {
  const [ed, setEd] = React.useState(null);
  const [ask, setAsk] = React.useState(false);
  return (
    <li className={`six-an-c ${c.enAttente ? "is-pending" : ""}`}>
      <div className="six-an-c-h">
        <b>{c.auteur}</b>
        <span>{dateHeure(c.creeLe)}</span>
        {c.modifieLe && <em>modifié le {dateHeure(c.modifieLe)}</em>}
        {c.enAttente && <em>Envoi…</em>}
        {c.moi && !c.enAttente && !ed && !ask && (
          <span className="six-an-c-act">
            <button className="mf-link" onClick={() => setEd(c.texte)}>Modifier</button>
            <button className="mf-link six-an-del" onClick={() => setAsk(true)}>Supprimer</button>
          </span>
        )}
      </div>
      {ed != null ? (
        <>
          <textarea className="mf-input mf-note six-an-ta" value={ed} onChange={(e) => setEd(e.target.value)} aria-label="Modifier le commentaire" autoFocus />
          <div className="six-an-bar">
            <button className="btn" onClick={() => setEd(null)}>Annuler</button>
            <button
              className="btn primary"
              disabled={!ed.trim() || ed.trim() === c.texte}
              onClick={() => {
                onModifier(c, ed);
                setEd(null);
              }}
            >
              Enregistrer
            </button>
          </div>
        </>
      ) : (
        <p>{c.texte}</p>
      )}
      {ask && (
        <span className="sys-ask">
          Supprimer ce commentaire ?
          <button className="btn sys-btn-del" onClick={() => onSupprimer(c)}>Supprimer</button>
          <button className="btn" onClick={() => setAsk(false)}>Annuler</button>
        </span>
      )}
    </li>
  );
}

const Squelette = () => (
  <div aria-hidden="true" className="six-an-sk">
    <span className="six-sk" style={{ width: "100%", height: 96 }} />
    <span className="six-sk" style={{ width: 220 }} />
    {[0, 1].map((i) => (
      <div key={i} className="six-an-c">
        <span className="six-sk" style={{ width: 180 }} />
        <span className="six-sk" style={{ width: "90%" }} />
      </div>
    ))}
  </div>
);

/**
 * Panneau latéral : note + commentaires d'un élément.
 * onChange(ref, { note, commentaires }) met à jour la pastille de la ligne.
 */
export function PanneauAnnotations({ cible, refId, type, titre, onClose, onChange, notifier }) {
  const [data, setData] = React.useState(null);
  const [note, setNote] = React.useState("");
  const [nouveau, setNouveau] = React.useState("");

  React.useEffect(() => {
    let actif = true;
    siApi
      .annotations(cible, refId)
      .then((d) => {
        if (!actif) return;
        setData(d);
        setNote(d.note?.texte || "");
      })
      .catch((e) => {
        if (!actif) return;
        notifier(e.message, { type: "erreur" });
        onClose();
      });
    return () => {
      actif = false;
    };
  }, [cible, refId, notifier, onClose]);

  React.useEffect(() => {
    const echap = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", echap);
    return () => window.removeEventListener("keydown", echap);
  }, [onClose]);

  // Pastille de la ligne toujours alignée sur ce qui est affiché.
  React.useEffect(() => {
    if (data) onChange(refId, { note: !!data.note, commentaires: data.commentaires.length });
  }, [data, refId, onChange]);

  // Tout s'affiche immédiatement, avec le message de confirmation ;
  // l'enregistrement suit en arrière-plan. En cas d'échec, l'élément touché
  // reprend son état d'avant (le reste du panneau est conservé) et un message
  // d'erreur le signale.
  const majCommentaires = (f) => setData((d) => ({ ...d, commentaires: f(d.commentaires) }));
  const echec = (e, quoi) => notifier(`${quoi} : ${e.message}`, { type: "erreur" });

  const noteModifiee = data && note.trim() !== (data.note?.texte || "");
  const enregistrerNote = async () => {
    const avant = data.note;
    const t = note.trim();
    const maintenant = new Date().toISOString();
    setData((d) => ({
      ...d,
      note: t ? { texte: t, creePar: avant?.creePar || moi(), creeLe: avant?.creeLe || maintenant, modifiePar: moi(), modifieLe: maintenant } : null,
    }));
    notifier(t ? "Note enregistrée" : "Note effacée");
    try {
      const r = await siApi.enregistrerNote(cible, refId, t);
      setData((d) => ({ ...d, note: r.note }));
    } catch (e) {
      setData((d) => ({ ...d, note: avant }));
      setNote(avant?.texte || "");
      echec(e, "Note non enregistrée");
    }
  };
  const ajouter = async () => {
    const texte = nouveau.trim();
    const temporaire = { id: `tmp-${Date.now()}`, texte, auteur: moi(), creeLe: new Date().toISOString(), modifieLe: null, moi: true, enAttente: true };
    majCommentaires((l) => [temporaire, ...l]);
    setNouveau("");
    notifier("Commentaire ajouté");
    try {
      const c = await siApi.ajouterCommentaire(cible, refId, texte);
      majCommentaires((l) => l.map((x) => (x.id === temporaire.id ? c : x)));
    } catch (e) {
      majCommentaires((l) => l.filter((x) => x.id !== temporaire.id));
      setNouveau((v) => v || texte); // le texte n'est pas perdu
      echec(e, "Commentaire non ajouté");
    }
  };
  const modifier = async (c, texte) => {
    const t = texte.trim();
    majCommentaires((l) => l.map((x) => (x.id === c.id ? { ...x, texte: t, modifieLe: new Date().toISOString() } : x)));
    notifier("Commentaire modifié");
    try {
      const m = await siApi.modifierCommentaire(c.id, t);
      majCommentaires((l) => l.map((x) => (x.id === c.id ? m : x)));
    } catch (e) {
      majCommentaires((l) => l.map((x) => (x.id === c.id ? c : x)));
      echec(e, "Commentaire non modifié");
    }
  };
  const supprimer = async (c) => {
    const position = data.commentaires.findIndex((x) => x.id === c.id);
    majCommentaires((l) => l.filter((x) => x.id !== c.id));
    notifier("Commentaire supprimé");
    try {
      await siApi.supprimerCommentaire(c.id);
    } catch (e) {
      majCommentaires((l) => [...l.slice(0, position), c, ...l.slice(position)]);
      echec(e, "Commentaire non supprimé");
    }
  };

  const n = data?.commentaires.length || 0;
  return (
    <div className="six-an-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="six-an-panel" role="dialog" aria-modal="true" aria-label={`Notes et commentaires — ${titre}`}>
        <div className="mf-head">
          <div>
            <div className="mf-meta"><b>{type}</b><i></i>Notes et commentaires</div>
            <h2>{titre}</h2>
          </div>
          <div className="mf-actions">
            <button className="mf-x" onClick={onClose} title="Fermer"><Icon name="x" size={14} /></button>
          </div>
        </div>
        <div className="mf-body">
          {!data ? (
            <Squelette />
          ) : (
            <>
              <div className="mf-card">
                <div className="mf-card-h"><span className="mf-ico"><Icon name="fileText" size={14} /></span>Note</div>
                <textarea
                  className="mf-input mf-note six-an-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Procédure, contacts, particularités…"
                  aria-label="Note"
                  maxLength={5000}
                />
                {data.note && (
                  <p className="six-an-meta">
                    Créée le {dateHeure(data.note.creeLe)} par {data.note.creePar}
                    {data.note.modifieLe && dateHeure(data.note.modifieLe) !== dateHeure(data.note.creeLe) && <> · Modifiée le {dateHeure(data.note.modifieLe)} par {data.note.modifiePar}</>}
                  </p>
                )}
                <div className="six-an-bar">
                  <span />
                  <button className="btn primary" disabled={!noteModifiee} onClick={enregistrerNote}>Enregistrer</button>
                </div>
              </div>

              <div className="mf-card">
                <div className="mf-card-h"><span className="mf-ico"><Icon name="message" size={14} /></span>Commentaires{n > 0 && <span className="six-an-cnt">{n}</span>}</div>
                <textarea
                  className="mf-input mf-note six-an-ta"
                  value={nouveau}
                  onChange={(e) => setNouveau(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && nouveau.trim()) {
                      e.preventDefault();
                      ajouter();
                    }
                  }}
                  placeholder="Ajouter un commentaire… (Ctrl + Entrée)"
                  aria-label="Nouveau commentaire"
                  maxLength={2000}
                />
                <div className="six-an-bar">
                  <span />
                  <button className="btn primary" disabled={!nouveau.trim()} onClick={ajouter}>Ajouter</button>
                </div>
                {n > 0 ? (
                  <ul className="six-an-list">
                    {data.commentaires.map((c) => (
                      <Commentaire key={c.id} c={c} onModifier={modifier} onSupprimer={supprimer} />
                    ))}
                  </ul>
                ) : (
                  <p className="six-an-vide">Aucun commentaire pour l'instant.</p>
                )}
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
